import { useQuery } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { productsApi } from '@/api/products'
import { ListPagination, DEFAULT_PAGE_SIZE } from '@/components/common/ListPagination'
import { useState } from 'react'
import { cn } from '@/lib/utils'

interface StockRecordDialogProps {
	productId: number | null
	onClose: () => void
}

export function StockRecordDialog({ productId, onClose }: StockRecordDialogProps) {
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['product-stock-records', productId, page, pageSize],
		queryFn: () => productsApi.listStockRecords(productId!, page, pageSize),
		enabled: productId !== null,
	})

	const handleClose = () => { setPage(1); onClose() }

	return (
		<Dialog open={productId !== null} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
				<DialogHeader><DialogTitle>库存变更记录</DialogTitle></DialogHeader>
				{isLoading ? (
					<div className="py-8 text-center text-muted-foreground">加载中...</div>
				) : (
					<div className="space-y-3">
						<div className="border rounded-lg overflow-hidden">
							<table className="w-full text-sm">
								<thead className="bg-muted/50">
									<tr>
										<th className="text-left p-2">规格</th>
										<th className="text-center p-2">方向</th>
										<th className="text-right p-2">数量</th>
										<th className="text-right p-2">成本价</th>
										<th className="text-right p-2">时间</th>
									</tr>
								</thead>
								<tbody>
									{data?.list.length === 0 && (
										<tr><td colSpan={5} className="text-center p-6 text-muted-foreground">暂无记录</td></tr>
									)}
									{data?.list.map((record) => (
										<tr key={record.id} className="border-t">
											<td className="p-2">{record.suk || record.unique}</td>
											<td className="text-center p-2">
												<span className={cn(
													'text-xs px-2 py-0.5 rounded',
													record.pm === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
												)}>
													{record.pm === 1 ? '入库' : '出库'}
												</span>
											</td>
											<td className={cn('text-right p-2 font-medium', record.pm === 1 ? 'text-green-600' : 'text-red-600')}>
												{record.pm === 1 ? '+' : '-'}{record.number}
											</td>
											<td className="text-right p-2 text-muted-foreground">¥{record.cost_price}</td>
											<td className="text-right p-2 text-muted-foreground text-xs">
												{record.add_time ? new Date(record.add_time * 1000).toLocaleString('zh-CN') : '-'}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						{data && data.total > 0 && (
							<ListPagination
								page={page}
								pageSize={pageSize}
								total={data.total}
								loading={isFetching}
								onPageChange={setPage}
								onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
							/>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
